  /**
   * Speech-to-Text service using Web Speech API
   * This service handles converting speech to text for faster Lex backend communication
   */

class SpeechToTextService {
  constructor() {
    this.recognition = null;
    this.isListening = false;
    this.onResult = null;
    this.onError = null;
    this.onStart = null;
    this.onEnd = null;
    this.finalTranscript = '';
    this.interimTranscript = '';
    this.handsFreeMode = false; // New property for hands-free mode
    this.autoStopTimeout = null; // Timeout for auto-stopping in hands-free mode
  }  /**
   * Check if Speech Recognition is supported
   */
  isSupported() {
    return !!(window.SpeechRecognition || window.webkitSpeechRecognition);
  }

  /**
   * Initialize speech recognition
   */
  init() {
    if (!this.isSupported()) {
      console.error('Speech Recognition is not supported in this browser');
      return false;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    this.recognition = new SpeechRecognition();
    
    // Configuration for hands-free conversational experience
    this.recognition.continuous = true;
    this.recognition.interimResults = true;
    this.recognition.lang = 'en-US';
    this.recognition.maxAlternatives = 1;
    
    // Optimized settings for hands-free mode
    if (this.recognition.serviceURI) {
      // For browsers that support it, use cloud-based recognition for better accuracy
      this.recognition.serviceURI = 'wss://speech.googleapis.com/v1/speech:recognize';
    }
    
    // Add timeout for better user experience in hands-free mode
    this.silenceTimer = null;
    this.finalResultTimer = null;
    this.handsFreeRestartTimer = null;

    // Event handlers
    this.recognition.onstart = () => {
      console.log('Speech recognition started');
      this.isListening = true;
      if (this.onStart) this.onStart();
    };

    this.recognition.onresult = (event) => {
      let interimTranscript = '';
      let finalTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }

      this.finalTranscript = finalTranscript;
      this.interimTranscript = interimTranscript;

      if (this.onResult) {
        this.onResult({
          final: finalTranscript,
          interim: interimTranscript,
          isFinal: !!finalTranscript
        });
      }

      // Auto-stop after getting final result, optimized for hands-free conversations
      if (finalTranscript.trim()) {
        if (this.finalResultTimer) {
          clearTimeout(this.finalResultTimer);
        }
        
        if (this.handsFreeMode) {
          // In hands-free mode, stop faster but allow for immediate restart
          this.finalResultTimer = setTimeout(() => {
            console.log('🎙️ Hands-free mode: Stopping recognition to allow immediate restart');
            this.stopListening();
          }, 800); // Faster stop in hands-free mode for quicker turnaround
        } else {
          // In normal mode, stop quickly after final result
          this.finalResultTimer = setTimeout(() => {
            this.stopListening();
          }, 500);
        }
      }
    };

    this.recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      this.isListening = false;
      
      // Don't propagate aborted errors as they are usually user-initiated
      if (event.error === 'aborted') {
        console.log('Speech recognition was aborted (likely user-initiated)');
        return;
      }
      
      let errorMessage = 'Speech recognition error';
      switch (event.error) {
        case 'no-speech':
          errorMessage = 'No speech was detected. Please try again.';
          break;
        case 'audio-capture':
          errorMessage = 'Audio capture failed. Please check your microphone.';
          break;
        case 'not-allowed':
          errorMessage = 'Microphone access denied. Please allow microphone access.';
          break;
        case 'network':
          errorMessage = 'Network error occurred during speech recognition.';
          break;
        case 'service-not-allowed':
          errorMessage = 'Speech recognition service is not available.';
          break;
        case 'bad-grammar':
          errorMessage = 'Grammar error in speech recognition.';
          break;
        case 'language-not-supported':
          errorMessage = 'Language not supported for speech recognition.';
          break;
        default:
          errorMessage = `Speech recognition error: ${event.error}`;
      }

      if (this.onError) {
        this.onError(new Error(errorMessage));
      }
    };

    this.recognition.onend = () => {
      console.log('Speech recognition ended');
      this.isListening = false;
      
      // In hands-free mode, provide faster callback for immediate restart capability
      if (this.handsFreeMode) {
        console.log('🎙️ Hands-free mode: Speech ended - ready for immediate restart');
        // Immediate callback in hands-free mode for faster turnaround
        if (this.onEnd) {
          this.onEnd();
        }
      } else if (this.onEnd) {
        // Delay the onEnd callback slightly in normal mode
        setTimeout(() => {
          if (this.onEnd) {
            this.onEnd();
          }
        }, 100);
      }
    };

    return true;
  }

  /**
   * Start listening for speech
   */
  startListening(callbacks = {}, handsFreeMode = false) {
    if (!this.recognition) {
      if (!this.init()) {
        if (callbacks.onError) {
          callbacks.onError(new Error('Speech recognition not available'));
        }
        return false;
      }
    }

    if (this.isListening) {
      console.log('Already listening...');
      return true;
    }

    // Set hands-free mode
    this.handsFreeMode = handsFreeMode;

    // Set callbacks
    this.onResult = callbacks.onResult;
    this.onError = callbacks.onError;
    this.onStart = callbacks.onStart;
    this.onEnd = callbacks.onEnd;

    // Reset transcripts
    this.finalTranscript = '';
    this.interimTranscript = '';

    try {
      this.recognition.start();
      return true;
    } catch (error) {
      console.error('Error starting speech recognition:', error);
      if (this.onError) {
        this.onError(error);
      }
      return false;
    }
  }

  /**
   * Stop listening for speech
   */
  stopListening() {
    if (this.recognition && this.isListening) {
      console.log('Stopping speech recognition...');
      this.isListening = false; // Set this first to prevent race conditions
      this.recognition.stop();
    }
  }

  /**
   * Abort speech recognition (immediate stop)
   */
  abort() {
    if (this.recognition) {
      console.log('Aborting speech recognition...');
      this.isListening = false; // Set this first to prevent race conditions
      this.recognition.abort();
      
      // Clear all timers on abort
      if (this.finalResultTimer) {
        clearTimeout(this.finalResultTimer);
        this.finalResultTimer = null;
      }
      if (this.silenceTimer) {
        clearTimeout(this.silenceTimer);
        this.silenceTimer = null;
      }
    }
  }

  /**
   * Get current hands-free mode state
   */
  getHandsFreeMode() {
    return this.handsFreeMode;
  }

  /**
   * Set hands-free mode
   */
  setHandsFreeMode(enabled) {
    this.handsFreeMode = enabled;
  }

  /**
   * Get current listening state
   */
  getIsListening() {
    return this.isListening;
  }

  /**
   * Get the final transcript
   */
  getFinalTranscript() {
    return this.finalTranscript;
  }

  /**
   * Get the interim transcript
   */
  getInterimTranscript() {
    return this.interimTranscript;
  }

  /**
   * Clean up resources
   */
  cleanup() {
    if (this.recognition) {
      this.recognition.abort();
      this.recognition = null;
    }
    
    // Clear timers
    if (this.silenceTimer) {
      clearTimeout(this.silenceTimer);
      this.silenceTimer = null;
    }
    if (this.finalResultTimer) {
      clearTimeout(this.finalResultTimer);
      this.finalResultTimer = null;
    }
    if (this.autoStopTimeout) {
      clearTimeout(this.autoStopTimeout);
      this.autoStopTimeout = null;
    }
    if (this.handsFreeRestartTimer) {
      clearTimeout(this.handsFreeRestartTimer);
      this.handsFreeRestartTimer = null;
    }
    
    this.isListening = false;
    this.handsFreeMode = false;
    this.onResult = null;
    this.onError = null;
    this.onStart = null;
    this.onEnd = null;
  }
}

export default SpeechToTextService;
